import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import type { MyRegistration, MyRegistrationResponse, MyRegistrationsResponse } from './types';

/**
 * The slice's whole HTTP surface (`code-organization.md` — `api.ts` is the frontend's
 * `repository.ts`).
 *
 * **Everything here needs a session**, which is the difference from `event-discovery`'s two
 * deliberately unauthenticated-capable calls. `apiRequest` attaches a bearer token when one is in
 * storage and omits it otherwise, so a signed-out caller reaches these and is refused by the API —
 * correctly. The client's job is to not draw the button; the control is the server's.
 *
 * **No path carries a member id**, matching the API. The member is the session, so the queries are
 * keyed by event alone.
 */

const registrationKey = (eventId: string): readonly unknown[] => [
  'event-registrations',
  'event',
  eventId,
];
const MINE_KEY = ['event-registrations', 'mine'] as const;

/**
 * **Every read whose answer depends on whether the member is registered for an event** — and the
 * one function that refreshes them when that changes.
 *
 * ## The defect this exists for
 *
 * Sumit, on the deployed dev site, 2026-09-12: *"when I go back to event after reserving spot,
 * the who's coming list does not open untill I refresh the page."* The event page showed the
 * registered header card and *YOU'RE GOING*, and the sidebar beside it still read *"Reserve your
 * spot to see who's coming."*
 *
 * Nothing was wrong with either answer. Since his Who's coming ruling of 2026-09-11 the API
 * **refuses the roster to a non-registrant** and serves a `registrants-only` closure instead
 * (`MEM-011` AC-1); the client had cached that refusal before the member reserved, and
 * `useRegisterForEvent` invalidated exactly two keys — the registration panel's and the member's
 * own list. The roster's key was not one of them, so a correct locked payload went on being
 * rendered next to a correct registered one until a reload emptied the cache.
 *
 * ## Why a list of slices rather than a list of keys
 *
 * Because the list of keys is the thing that goes stale. `attendee-preview` alone has four reads —
 * the roster, an attendee profile, the meet list and the Discover cards' going counts — and every
 * one of them answers differently for a registrant; a fifth would have to be remembered here by
 * whoever added it. React Query matches a `queryKey` by prefix, so naming the **slice** covers the
 * reads it has today and the ones it grows, and the cost of being broad is a refetch of data the
 * member is looking at anyway, once, at the moment they reserve.
 *
 * Only *active* queries refetch immediately; the rest are marked stale and re-read when their
 * screen next mounts, which is exactly the *"go back to the event"* case in the report.
 *
 * ## Why it is one function and not three
 *
 * There are three mutations that change registration state — reserving a free place, paying for a
 * checkout, and cancelling — and they live in two slices. Three copies of this list is three
 * places for it to drift, and it had already: `usePayForCheckout` invalidated
 * `['event-registrations', eventId]`, which is not a key this app has (the registration read is
 * keyed `['event-registrations', 'event', eventId]`), so the **paid** path refreshed nothing at
 * all — including the registration panel the free path did remember. That is a second bug of the
 * same shape found while fixing the first.
 *
 * `the-roster-opens-after-reserving.test.tsx` asserts the call from all three.
 *
 * **It lives in `api.ts` rather than in a module of its own** because
 * `docs/standards/code-organization.md` fixes a domain slice to `api.ts` / `types.ts` /
 * `constants.ts` plus components, and this is cache policy for exactly the HTTP surface this
 * file is — the frontend `repository.ts`. `event-orders` imports it from here.
 *
 * ## What is deliberately not here
 *
 * **`event-discovery`.** The event's own payload is the same for a registrant and a stranger: it
 * carries no going count (`MEM-011` owns that figure, and it is `attendee-preview`'s own read) and
 * no seats-remaining figure (`event-orders/types.ts` says why there will not be one). Invalidating
 * it would refetch the Discover list and every search result to change nothing.
 *
 * **`connections`, `messages` and the member's own profile.** A connection is between two members
 * rather than between two members at an evening (`MEM-033`), so cancelling a registration does not
 * change one. The follow-up index is here because a follow-up is built from an event a member
 * actually attended.
 */
export const REGISTRATION_DEPENDENT_SLICES: ReadonlyArray<readonly [string]> = [
  /** The registration itself, and the member's list of them. */
  ['event-registrations'],
  /** The roster and its closure, an attendee profile's disclosure level, the meet list, the going counts. */
  ['attendee-preview'],
  /** A released match set is served to registrants (`MEM-013` AC-6 removes access with the place). */
  ['matching'],
  /** The cancellation preview, Your Events' rows, the receipt. */
  ['event-orders'],
  /** A ticket exists because a registration does. */
  ['event-tickets'],
  /** The in-event surface — the connect code, the room — all behind a confirmed place. */
  ['member-in-event'],
  /** Follow-ups are built from events the member attended. */
  ['follow-up'],
];

/**
 * Mark every registration-dependent read stale, and refetch the ones on screen.
 *
 * Awaited by its callers so a mutation's `onSuccess` does not resolve before the screen behind it
 * has been told — the difference between a member seeing the roster open and seeing it open a
 * moment after they have already looked away.
 */
export async function refreshRegistrationDependentReads(queryClient: QueryClient): Promise<void> {
  await Promise.all(
    REGISTRATION_DEPENDENT_SLICES.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}

/**
 * `MEM-017` AC-5: the registered state, resolved server-side.
 *
 * `enabled` is how a signed-out visitor is kept from firing a call that can only 401. It is a
 * courtesy, not a control — see the file header.
 */
export function useMyRegistration(
  eventId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<MyRegistrationResponse> {
  return useQuery({
    queryKey: registrationKey(eventId),
    queryFn: () =>
      apiRequest<MyRegistrationResponse>(
        `/api/event-registrations/events/${encodeURIComponent(eventId)}/mine`,
      ),
    enabled: options.enabled && eventId.length > 0,
    // A 401 is a final answer, not a transient failure.
    retry: false,
  });
}

/** What the member is going to. */
export function useMyRegistrations(options: {
  readonly enabled: boolean;
}): UseQueryResult<MyRegistrationsResponse> {
  return useQuery({
    queryKey: MINE_KEY,
    queryFn: () => apiRequest<MyRegistrationsResponse>('/api/event-registrations/mine'),
    enabled: options.enabled,
    retry: false,
  });
}

/**
 * Register for a free event.
 *
 * The API answers `409` for a full event and for a member who already holds a place, and `400`
 * for a closed window or a paid event — each with the sentence the member should read. The
 * component surfaces that message rather than composing its own, so the refusal a member sees is
 * the one the server actually applied.
 */
export function useRegisterForEvent(
  eventId: string,
): UseMutationResult<{ registration: MyRegistration }, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ registration: MyRegistration }>(
        `/api/event-registrations/events/${encodeURIComponent(eventId)}`,
        { method: 'POST' },
      ),
    onSuccess: async () => {
      /*
        **Everything a registration decides, not only the panel** — Sumit, 2026-09-12: *"when I go
        back to event after reserving spot, the who's coming list does not open untill I refresh
        the page."* This invalidated the registration read and the member's own list and stopped
        there, so the roster's cached `registrants-only` refusal outlived the reservation that
        answered it. The list is `registration-dependent-reads.ts`, shared with the paid path and
        the cancellation so the three cannot drift.
      */
      await refreshRegistrationDependentReads(queryClient);
    },
  });
}

/** Give up a place. Free only, so there is no refund to explain. */
export function useCancelMyRegistration(
  eventId: string,
): UseMutationResult<{ registration: MyRegistration }, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ registration: MyRegistration }>(
        `/api/event-registrations/events/${encodeURIComponent(eventId)}`,
        { method: 'DELETE' },
      ),
    onSuccess: async () => {
      // The same list, in the other direction: the roster has to **lock again** without a reload,
      // and the meet list and the match set go with the place (`MEM-013` AC-6).
      await refreshRegistrationDependentReads(queryClient);
    },
  });
}
